import { Button, Div, Text } from "atomize"
import { useContext, useEffect, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleDown, faWandSparkles } from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import Divider from "../atoms/Divider"
import PlayerLessonRow from "./PlayerLessonRow"
import usePlayerLessons from "./usePlayerLessons"
import usePlayerSeasonAbsences from "./usePlayerSeasonAbsences"
import {
	canEditLesson,
	getLessonsHistoryStartLabel,
	getLessonTypeTileColors,
	lessonTypeDisplayNameOf,
} from "../utils/func.utils"
import { absencesToRecover, seasonLabel } from "../utils/substitutes.utils"

const PLAYER_LESSONS_PREVIEW = 10

// Ultime lezioni dell'allievo dentro la sua scheda: le altre si sfogliano nel
// modal dedicato. Si monta solo quando si apre la sezione, quindi le letture
// partono da lì
function PlayerLessonsList({ playerData, version }) {
	const Modal = useContext(modalContext)
	const { coach } = useContext(profileContext)
	const { selectedSchool } = useContext(schoolContext)

	const { lessons, isLoading, hasMore, hasError, reload } = usePlayerLessons(playerData, {
		pageSize: PLAYER_LESSONS_PREVIEW,
	})

	// una lezione cambiata mentre l'elenco è aperto: lo rileggo
	const versionRef = useRef(version)
	useEffect(() => {
		if (versionRef.current === version) return
		versionRef.current = version
		reload()
	}, [version, reload])

	const handleLessonClick = lessonData =>
		canEditLesson(coach, selectedSchool, lessonData)
			? () => Modal.open("editLesson", lessonData)
			: undefined

	return (
		<>
			{isLoading ? (
				<Text textColor={theme.colors.text_light} p={{ y: "0.5rem" }}>
					Sto cercando le lezioni... 🧐
				</Text>
			) : hasError ? (
				<Text textColor={theme.colors.text_light} p={{ y: "0.5rem" }}>
					Non sono riuscito a leggere le lezioni
				</Text>
			) : lessons.length === 0 ? (
				<Text textColor={theme.colors.text_light} p={{ y: "0.5rem" }}>
					Nessuna lezione da {getLessonsHistoryStartLabel()}
				</Text>
			) : (
				<>
					{lessons.map((lesson, i) => (
						<Div key={lesson.lessonID}>
							<PlayerLessonRow
								lessonData={lesson}
								playerData={playerData}
								onClick={handleLessonClick(lesson)}
							/>
							{i < lessons.length - 1 && (
								<Divider w="2px solid" borderColor={theme.colors.surface} />
							)}
						</Div>
					))}
					{hasMore && (
						<motion.div whileTap={{ scale: 0.95 }}>
							<Button
								w="100%"
								h="2.75rem"
								m={{ t: "0.75rem" }}
								bg={theme.colors.surface}
								textColor={theme.colors.gunmetal}
								textWeight="bold"
								rounded={theme.rounded["15px"]}
								onClick={() => Modal.open("playerLessons", playerData)}>
								Vedi tutte le lezioni
							</Button>
						</motion.div>
					)}
				</>
			)}
		</>
	)
}

// Le assenze e i recuperi della stagione, una riga per corso: un corso adulti
// saltato si recupera solo nel corso adulti, la scuola tennis nella scuola
// tennis. Toccando una riga si apre il foglio con le sue assenze e i suoi
// recuperi di quel corso, lo stesso del riepilogo nel profilo.
function SeasonAbsences({ courses, season, onChanged }) {
	const Modal = useContext(modalContext)

	if (courses.length === 0) return null

	return (
		<Div
			bg={theme.colors.surface}
			rounded="15px"
			p={{ x: "0.75rem" }}
			m={{ t: "0.5rem", b: "0.25rem" }}>
			{courses.map(({ course, name, colors, entry }, i) => {
				const left = absencesToRecover(entry)
				const absences = entry.absences.length
				const recoveries = entry.recoveries.length
				const elsewhere = entry.elsewhere?.length ?? 0
				const detail = [
					absences > 0 && `${absences} ${absences === 1 ? "assenza" : "assenze"}`,
					recoveries > 0 && `${recoveries} ${recoveries === 1 ? "recuperata" : "recuperate"}`,
					elsewhere > 0 && `${elsewhere} altrove`,
				]
					.filter(Boolean)
					.join(" · ")

				return (
					<motion.div key={course} whileTap={{ scale: 0.98 }}>
						<Div
							tag="button"
							type="button"
							w="100%"
							minH="2.75rem"
							d="flex"
							align="center"
							bg="transparent"
							cursor="pointer"
							p={{ y: "0.5rem", x: "0" }}
							style={{
								gap: "0.6rem",
								border: 0,
								borderTop: i === 0 ? 0 : `1px solid ${theme.colors.background}`,
								font: "inherit",
								textAlign: "left",
							}}
							onClick={() =>
								Modal.open("playerAbsences", {
									...entry,
									periodText: `${name} · ${seasonLabel(season)}`,
									// segnata un'assenza recuperata altrove, si rilegge
									onChanged,
								})
							}>
							<Text
								tag="span"
								textSize="caption"
								textWeight="bold"
								bg={colors.bg}
								textColor={colors.text}
								p={{ x: "0.6rem", y: "0.1rem" }}
								rounded="15px"
								style={{ whiteSpace: "nowrap" }}>
								{name}
							</Text>
							{/* a destra, come pagamento e nota nelle righe delle lezioni sotto */}
							<Div tag="span" m={{ l: "auto" }} textAlign="right">
								<Text
									textSize="caption"
									textWeight="bold"
									textColor={left > 0 ? theme.colors.red : theme.colors.text_light}>
									{left > 0 ? `${left} da recuperare` : "tutto recuperato"}
								</Text>
								<Text textSize="caption" textColor={theme.colors.text_light}>
									{detail}
								</Text>
							</Div>
						</Div>
					</motion.div>
				)
			})}
		</Div>
	)
}

// L'allievo si apre quasi sempre per altro (contatti, scheda): le lezioni sono
// un blocco chiuso, e si leggono solo quando lo si apre. Il fondo è quello
// caldo dei blocchi dentro una scheda bianca, come il campo nella lezione.
//
// Le assenze invece si leggono appena si apre l'allievo, perché anche da
// chiuso il blocco dice quante lezioni ha da recuperare. È una lettura
// piccola: solo le sue lezioni con un'assenza o un recupero.
function PlayerLessons({ playerData }) {
	const Modal = useContext(modalContext)
	const { getLessonType } = useContext(sharedDataContext)
	const [isOpen, setOpen] = useState(false)

	// se sono passato dalla modifica (o dall'eliminazione) di una lezione,
	// quando il modal si chiude rileggo lo storico e le assenze
	const [version, setVersion] = useState(0)
	const lastModalTypeRef = useRef("")
	useEffect(() => {
		if (Modal.type) {
			lastModalTypeRef.current = Modal.type
			return
		}
		if (["editLesson", "deleteLesson", "playerLessons"].includes(lastModalTypeRef.current)) {
			lastModalTypeRef.current = ""
			setVersion(v => v + 1)
		}
	}, [Modal.type])

	const { byCourse, season } = usePlayerSeasonAbsences(playerData.playerID, version)
	// sempre nello stesso ordine, per nome, come nel riepilogo del profilo
	const courses = Object.entries(byCourse)
		.map(([course, entry]) => ({
			course,
			name: lessonTypeDisplayNameOf(getLessonType(course), course.toUpperCase()),
			// i colori del corso, come nelle sue lezioni
			colors: getLessonTypeTileColors(getLessonType(course)),
			entry,
		}))
		.sort((a, b) => a.name.localeCompare(b.name, "it"))
	const toRecover = courses.reduce((sum, { entry }) => sum + absencesToRecover(entry), 0)

	return (
		<Div bg={theme.colors.background} rounded="15px" m={{ t: "0.25rem", x: "0.5rem" }}>
			<Div
				d="flex"
				align="center"
				p="0.75rem 1rem"
				cursor="pointer"
				style={{ gap: "0.6rem" }}
				onClick={() => setOpen(!isOpen)}>
				<FontAwesomeIcon icon={faWandSparkles} color={theme.colors.primary} />
				<Text textWeight="bold" textColor={theme.colors.gunmetal}>
					Lezioni
				</Text>
				{toRecover > 0 && (
					<Text
						m={{ l: "auto" }}
						textSize="caption"
						textWeight="bold"
						textColor={theme.colors.red}>
						{toRecover} da recuperare
					</Text>
				)}
				<motion.div
					animate={{ rotate: isOpen ? 180 : 0 }}
					transition={{ duration: 0.25 }}
					style={{ display: "flex", marginLeft: toRecover > 0 ? 0 : "auto" }}>
					<FontAwesomeIcon icon={faAngleDown} color={theme.colors.text_light} />
				</motion.div>
			</Div>

			<AnimatePresence initial={false}>
				{isOpen && (
					<motion.section
						style={{ overflow: "hidden" }}
						initial="collapsed"
						animate="open"
						exit="collapsed"
						variants={{
							open: { opacity: 1, height: "auto" },
							collapsed: { opacity: 0, height: 0 },
						}}
						transition={{ duration: 0.25, ease: [0.04, 0.62, 0.23, 0.98] }}>
						<Div p={{ x: "1rem", b: "0.75rem" }}>
							<Div h="1px" bg={theme.colors.surface} m={{ b: "0.25rem" }} />
							<SeasonAbsences
								courses={courses}
								season={season}
								onChanged={() => setVersion(v => v + 1)}
							/>
							<PlayerLessonsList playerData={playerData} version={version} />
						</Div>
					</motion.section>
				)}
			</AnimatePresence>
		</Div>
	)
}

export default PlayerLessons
